import { queryOptions, useMutation, useQuery } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import {
  ApiCompetition,
  ApiCompetitionBulkAttemptData,
  ApiCompetitionBulkAttemptPayload,
  ApiCompetitionBulkAttemptsResponse,
  ApiCompetitionCreateEntryPayload,
  ApiCompetitionCreateEntryWithProfilePayload,
  ApiCompetitionDetail,
  ApiCompetitionEntryDetail,
  ApiCompetitionEntryInline,
  ApiCompetitionRouteCustomScoringConfig,
  ApiCompetitionUpdateEntryPayload,
  ApiCreateCategoryPayload,
  ApiCreateCompetitionPayload,
  ApiCreateLeaguePayload,
  ApiLeague,
  ApiLeagueEntry,
  ApiLeagueEntryInline,
  ApiUpdateCompetitionPayload,
  ApiUpdateLeaguePayload,
} from "griptonite-api-client";
import { orderBy } from "lodash";
import { useCallback } from "react";

import api from "@/core/api";
import queryClient from "@/core/queryClient";
import { AttemptsBatcher, createAttemptsBatcher } from "@/utils/createAttemptsBatcher";

const batcherCache = new Map<string, AttemptsBatcher>();

function getAttemptsBatcher(competitionUuid: string): AttemptsBatcher {
  if (!batcherCache.has(competitionUuid)) {
    batcherCache.set(competitionUuid, createAttemptsBatcher(competitionUuid));
  }
  return batcherCache.get(competitionUuid)!;
}

export const useCurrentCompetitionUuid = () => {
  return useParams({ strict: false }).competitionUuid;
};

export const useCurrentLeagueUuid = () => {
  return useParams({ strict: false }).leagueUuid;
};

// The first page covers the currently and recently active competitions most
// screens need. Pass allPages where older ones matter, e.g. the archive. The
// all-pages key extends the first-page key, so invalidating the venue's
// competitions refreshes both. While all pages load, a cached first page is
// shown as a placeholder; it isn't used as initialData because staleTime would
// then treat the partial list as fresh and delay fetching the rest.
export const useVenueCompetitionsQuery = <TData = ApiCompetition[]>(
  venueUuid?: string,
  select?: (data: ApiCompetition[]) => TData,
  { allPages = false }: { allPages?: boolean } = {},
) => {
  return useQuery({
    queryKey: allPages
      ? ["venues", venueUuid, "competitions", "allPages"]
      : ["venues", venueUuid, "competitions"],
    queryFn: async () => {
      if (allPages) {
        return api.collectPages(() => api.venues.getVenueCompetitions(venueUuid!));
      }
      const result = await api.venues.getVenueCompetitions(venueUuid!);
      return result.data;
    },
    enabled: !!venueUuid,
    placeholderData: allPages
      ? () => queryClient.getQueryData<ApiCompetition[]>(["venues", venueUuid, "competitions"])
      : undefined,
    select,
  });
};

export const useVenueLeaguesQuery = <TData = ApiLeague[]>(
  venueUuid?: string,
  select?: (data: ApiLeague[]) => TData,
) => {
  return useQuery({
    queryKey: ["venues", venueUuid, "leagues"],
    queryFn: async () => {
      const result = await api.venues.getVenueLeagues(venueUuid!);
      return result.data;
    },
    enabled: !!venueUuid,
    select,
  });
};

export const useVenueCompetitionsByUuid = (
  venueUuid?: string,
  options?: { allPages?: boolean },
) => {
  const select = useCallback((data: ApiCompetition[]) => {
    return Object.fromEntries(data.map((competition) => [competition.uuid, competition]));
  }, []);
  return useVenueCompetitionsQuery(venueUuid, select, options).data;
};

const findCompetitionInCache = (venueUuid: string, uuid: string) => {
  const matchingQueries = queryClient.getQueriesData<ApiCompetition[]>({
    queryKey: ["venues", venueUuid, "competitions"],
  });
  for (const [_, data] of matchingQueries) {
    if (data) {
      const competition = data.find((competition: ApiCompetition) => competition.uuid === uuid);
      if (competition) {
        return competition;
      }
    }
  }
  return undefined;
};

/** The competition's cached detail, or failing that its entry in the venue's list. */
export const getCachedCompetition = (competitionUuid: string, venueUuid?: string) =>
  queryClient.getQueryData<ApiCompetition & Partial<ApiCompetitionDetail>>([
    "competitions",
    competitionUuid,
  ]) ?? (venueUuid ? findCompetitionInCache(venueUuid, competitionUuid) : undefined);

export const useCompetitionQuery = (competitionUuid?: string, venueUuid?: string) => {
  return useQuery({
    queryKey: ["competitions", competitionUuid],
    queryFn: () =>
      api.competitions.getCompetition(competitionUuid!) as Promise<
        ApiCompetition & Partial<ApiCompetitionDetail>
      >,
    enabled: !!competitionUuid,
    placeholderData: () => {
      return findCompetitionInCache(venueUuid!, competitionUuid!) as
        | (ApiCompetition & Partial<ApiCompetitionDetail>)
        | undefined;
    },
  });
};

const competitionEntriesQuery = (competitionUuid?: string) =>
  queryOptions({
    queryKey: ["competitions", competitionUuid, "entries"],
    queryFn: () => api.competitions.getCompetitionEntries(competitionUuid!),
    enabled: !!competitionUuid,
  });

export const useCompetitionEntriesQuery = (competitionUuid?: string) => {
  return useQuery(competitionEntriesQuery(competitionUuid));
};

export const useCompetitionEntriesByUuid = (competitionUuid?: string) => {
  const select = useCallback((data: ApiCompetitionEntryInline[]) => {
    return Object.fromEntries(data.map((entry) => [entry.user.uuid, entry]));
  }, []);
  return useQuery({
    ...competitionEntriesQuery(competitionUuid),
    select,
  });
};

export const useCreateCompetitionMutation = () => {
  return useMutation({
    mutationFn: (payload: ApiCreateCompetitionPayload) =>
      api.competitions.createCompetition(payload),
    onSuccess: (data) => {
      localStorage.setItem(`competitions:${data.uuid}:hideWarnings:categories`, "true");
      localStorage.setItem(`competitions:${data.uuid}:hideWarnings:circuits`, "true");
      localStorage.setItem(`competitions:${data.uuid}:hideWarnings:scoring`, "true");
      queryClient.setQueryData(["competitions", data.uuid], data);
      queryClient.invalidateQueries({
        queryKey: ["venues", data.venues[0].uuid, "competitions"],
      });
      queryClient.invalidateQueries({
        queryKey: ["venues", data.venues[0].uuid, "leagues"],
      });
      if (data.league?.uuid) {
        queryClient.invalidateQueries({
          queryKey: ["leagues", data.league.uuid],
        });
      }
    },
  });
};

export const useUpdateCompetitionMutation = (
  competitionUuid?: string,
  venueUuid?: string,
  leagueUuid?: string,
) => {
  return useMutation({
    mutationFn: (payload: ApiUpdateCompetitionPayload) => {
      if (!competitionUuid) {
        throw new Error("Missing competitionUuid");
      }
      return api.competitions.updateCompetition(competitionUuid, payload);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["competitions", data.uuid],
      });
      queryClient.setQueryData(["competitions", data.uuid], data);
      queryClient.invalidateQueries({
        queryKey: ["venues", venueUuid, "competitions"],
      });
      queryClient.invalidateQueries({
        queryKey: ["venues", data.venues[0].uuid, "leagues"],
      });
      if (leagueUuid) {
        queryClient.invalidateQueries({
          queryKey: ["leagues", leagueUuid],
        });
      }
    },
  });
};

export const usePublishCompetitionResultsMutation = (venueUuid?: string, leagueUuid?: string) => {
  return useMutation({
    mutationFn: ([competitionUuid, stageUuid]: [string, string]) =>
      api.competitions.updateCompetitionStage(competitionUuid, stageUuid, {
        pendingResults: false,
      }),
    onSuccess: (_, [competitionUuid]) => {
      queryClient.invalidateQueries({
        queryKey: ["competitions", competitionUuid],
      });
      if (venueUuid) {
        queryClient.invalidateQueries({
          queryKey: ["venues", venueUuid, "competitions"],
        });
        // Needed because the competition routes are automatically converted to circuits for boulder comps
        queryClient.invalidateQueries({
          queryKey: ["venues", venueUuid, "routes"],
        });
      }
      if (leagueUuid) {
        queryClient.invalidateQueries({
          queryKey: ["leagues", leagueUuid],
        });
      }
    },
  });
};

export const useCreateCategoryMutation = (invalidationUuids: {
  competitionUuid?: string;
  venueUuid?: string;
  leagueUuid?: string;
}) => {
  return useMutation({
    mutationFn: (payload: ApiCreateCategoryPayload) =>
      api.competitions.createCompetitionCategory(payload),
    onSuccess: () => {
      const { competitionUuid, venueUuid, leagueUuid } = invalidationUuids;
      if (competitionUuid) {
        queryClient.invalidateQueries({
          queryKey: ["competitions", competitionUuid],
        });
      }
      if (leagueUuid) {
        queryClient.invalidateQueries({
          queryKey: ["leagues", leagueUuid],
        });
      }
      if (venueUuid) {
        queryClient.invalidateQueries({
          queryKey: ["venues", venueUuid, "competitions"],
        });
      }
    },
  });
};

export const useUpdateCategoryMutation = (
  categoryUuid?: string,
  invalidationUuids?: {
    competitionUuid?: string;
    venueUuid?: string;
    leagueUuid?: string;
  },
) => {
  return useMutation({
    mutationFn: (payload: Partial<ApiCreateCategoryPayload>) => {
      if (!categoryUuid) {
        throw new Error("Missing categoryUuid");
      }
      return api.competitions.updateCompetitionCategory(categoryUuid, payload);
    },
    onSuccess: () => {
      const { competitionUuid, venueUuid, leagueUuid } = invalidationUuids || {};
      if (competitionUuid) {
        queryClient.invalidateQueries({
          queryKey: ["competitions", competitionUuid],
        });
      }
      if (leagueUuid) {
        queryClient.invalidateQueries({
          queryKey: ["leagues", leagueUuid],
        });
      }
      if (venueUuid) {
        queryClient.invalidateQueries({
          queryKey: ["venues", venueUuid, "competitions"],
        });
      }
    },
  });
};

export const useDeleteCategoryMutation = (
  categoryUuid?: string,
  invalidationUuids?: {
    competitionUuid?: string;
    leagueUuid?: string;
    venueUuid?: string;
  },
) => {
  return useMutation({
    mutationFn: () => {
      if (!categoryUuid) {
        throw new Error("Missing categoryUuid");
      }
      return api.competitions.deleteCompetitionCategory(categoryUuid);
    },
    onSuccess: () => {
      const { competitionUuid, leagueUuid, venueUuid } = invalidationUuids || {};
      if (competitionUuid) {
        queryClient.invalidateQueries({
          queryKey: ["competitions", competitionUuid],
        });
      }
      if (leagueUuid) {
        queryClient.invalidateQueries({
          queryKey: ["leagues", leagueUuid],
        });
      }
      if (venueUuid) {
        queryClient.invalidateQueries({
          queryKey: ["venues", venueUuid, "competitions"],
        });
      }
    },
  });
};

export const useCompetitionCategoriesByUuid = (competitionUuid?: string) => {
  const select = useCallback((data: ApiCompetitionDetail) => {
    return Object.fromEntries(
      data.stages[0].categories.map((category) => [category.uuid, category]),
    );
  }, []);
  return useQuery({
    queryKey: ["competitions", competitionUuid],
    queryFn: () => api.competitions.getCompetition(competitionUuid!),
    enabled: !!competitionUuid,
    select,
  }).data;
};

export const useCompetitionAttemptsEntriesQuery = (competitionUuid?: string) => {
  return useQuery({
    queryKey: ["competitions", competitionUuid, "attempts"],
    queryFn: () => api.competitions.getAllCompetitionAttempts(competitionUuid!),
    enabled: !!competitionUuid,
  });
};

export const useCompetitionAttemptsByRouteUser = (competitionUuid?: string) => {
  const select = useCallback(({ attempts }: ApiCompetitionBulkAttemptsResponse) => {
    const result: Record<string, Record<string, ApiCompetitionBulkAttemptData>> = {};
    attempts = orderBy(attempts, ["route", "user", "timestamp"]);
    attempts.forEach((attempt) => {
      if (!result[attempt.route]) {
        result[attempt.route] = {};
      }
      result[attempt.route][attempt.user] = attempt;
    });
    return result;
  }, []);

  return useQuery({
    queryKey: ["competitions", competitionUuid, "attempts"],
    queryFn: () => api.competitions.getAllCompetitionAttempts(competitionUuid!),
    enabled: !!competitionUuid,
    select,
  }).data;
};

export const useCompetitionUserAttemptsByRoute = (competitionUuid?: string, userUuid?: string) => {
  const select = useCallback((attempts: ApiCompetitionBulkAttemptData[]) => {
    const result: Record<string, ApiCompetitionBulkAttemptData> = {};
    attempts.forEach((a) => {
      result[a.route] = a;
    });
    return result;
  }, []);

  return useQuery({
    queryKey: ["competitions", competitionUuid, "attempts", "user", userUuid],
    queryFn: () => getAttemptsBatcher(competitionUuid!).fetch(userUuid!),
    enabled: !!competitionUuid && !!userUuid,
    select,
    staleTime: 5 * 60 * 1000,
  });
};

export const useDeleteCompetitionMutation = (venueUuid?: string, leagueUuid?: string) => {
  return useMutation({
    mutationFn: (competitionUuid: string) => api.competitions.deleteCompetition(competitionUuid),
    onSuccess: (_, competitionUuid) => {
      if (venueUuid) {
        queryClient.invalidateQueries({
          queryKey: ["venues", venueUuid, "competitions"],
        });
        queryClient.invalidateQueries({
          queryKey: ["venues", venueUuid, "leagues"],
        });
      }
      if (leagueUuid) {
        queryClient.invalidateQueries({
          queryKey: ["leagues", leagueUuid],
        });
      }
      queryClient.invalidateQueries({
        queryKey: ["competitions", competitionUuid],
      });
    },
  });
};

export const useBulkUpdateCompetitionAttemptsMutation = () => {
  return useMutation({
    mutationFn: ({
      competitionUuid,
      attempts,
    }: {
      competitionUuid: string;
      attempts: ApiCompetitionBulkAttemptPayload;
    }) => api.competitions.bulkUpdateAttempts(competitionUuid, attempts),
    onSuccess: (_, { competitionUuid }) => {
      queryClient.invalidateQueries({
        queryKey: ["competitions", competitionUuid, "attempts"],
      });
      queryClient.invalidateQueries({
        predicate(query) {
          const key = query.queryKey;
          return (
            key[0] === "competitions" &&
            key[1] === competitionUuid &&
            key[2] === "attempts" &&
            key[3] === "user"
          );
        },
      });
      queryClient.invalidateQueries({
        queryKey: ["competitions", competitionUuid, "entries"],
      });
    },
  });
};

export const useCompetitionEntryQuery = (competitionUuid?: string, userUuid?: string) => {
  return useQuery({
    queryKey: ["competitions", competitionUuid, "entries", userUuid],
    queryFn: () =>
      api.competitions.getCompetitionEntry(competitionUuid!, userUuid!) as Promise<
        ApiCompetitionEntryDetail | ApiCompetitionEntryInline
      >,
    enabled: !!competitionUuid && !!userUuid,
    placeholderData: () => {
      const entries = queryClient.getQueryData<ApiCompetitionEntryInline[]>([
        "competitions",
        competitionUuid,
        "entries",
      ]);
      return entries?.find((entry) => entry.user.uuid === userUuid);
    },
  });
};

type UpdateEntryPayload = {
  competitionUuid: string;
  userUuid: string;
  data: ApiCompetitionUpdateEntryPayload;
};

export const useUpdateEntryMutation = () => {
  return useMutation({
    mutationFn: ({ competitionUuid, userUuid, data }: UpdateEntryPayload) =>
      api.competitions.updateCompetitionEntry(competitionUuid, userUuid, data),
    onSuccess: (entry, { competitionUuid }) => {
      queryClient.invalidateQueries({
        queryKey: ["competitions", competitionUuid, "entries"],
      });
      queryClient.setQueryData(
        ["competitions", competitionUuid, "entries", entry.user.uuid],
        entry,
      );
    },
  });
};

export const useDeleteEntryMutation = () => {
  return useMutation({
    mutationFn: ({ competitionUuid, userUuid }: { competitionUuid: string; userUuid: string }) =>
      api.competitions.deleteCompetitionEntry(competitionUuid, userUuid),
    onSuccess: (_, { competitionUuid }) => {
      queryClient.invalidateQueries({
        queryKey: ["competitions", competitionUuid, "entries"],
      });
    },
  });
};

export const useGenerateCompetitionLeaderboardPdfMutation = () => {
  return useMutation({
    mutationFn: async (competitionUuid: string) => {
      const { url } = await api.competitions.generateCompetitionLeaderboardPdf(competitionUuid);
      let triesCount = 0;
      while (triesCount < 10) {
        const result = await fetch(url, { method: "HEAD" });
        if (result.ok) {
          return url;
        } else {
          await new Promise((resolve) => setTimeout(resolve, 2000));
          ++triesCount;
        }
      }
      throw new Error("Failed to generate PDF");
    },
  });
};

export const useCreateCompetitionEntryMutation = () => {
  return useMutation({
    mutationFn: ({
      competitionUuid,
      ...payload
    }: { competitionUuid: string } & ApiCompetitionCreateEntryPayload) =>
      api.competitions.createCompetitionEntry(competitionUuid, payload),
    onSuccess: (_, { competitionUuid }) => {
      queryClient.invalidateQueries({
        queryKey: ["competitions", competitionUuid, "entries"],
      });
    },
  });
};

export const useCreateCompetitionEntryWithProfileMutation = () => {
  return useMutation({
    mutationFn: ({
      competitionUuid,
      ...payload
    }: {
      competitionUuid: string;
    } & ApiCompetitionCreateEntryWithProfilePayload) =>
      api.competitions.createCompetitionEntryWithProfile(competitionUuid, payload),
    onSuccess: (entry, { competitionUuid }) => {
      queryClient.invalidateQueries({
        queryKey: ["competitions", competitionUuid, "entries"],
      });
      queryClient.setQueryData(
        ["competitions", competitionUuid, "entries", entry.user.uuid],
        entry,
      );
    },
  });
};

export const useLeagueQuery = (leagueUuid?: string, venueUuid?: string) => {
  return useQuery({
    queryKey: ["leagues", leagueUuid],
    queryFn: () => api.competitions.getLeague(leagueUuid!),
    enabled: !!leagueUuid,
    placeholderData: () => {
      const leagues = queryClient.getQueryData<ApiLeague[]>(["venues", venueUuid, "leagues"]);
      return leagues?.find((league) => league.uuid === leagueUuid);
    },
  });
};

export const useCreateLeagueMutation = () => {
  return useMutation({
    mutationFn: (payload: ApiCreateLeaguePayload) => api.competitions.createLeague(payload),
    onSuccess: (data) => {
      queryClient.setQueryData(["leagues", data.uuid], data);
      queryClient.invalidateQueries({
        queryKey: ["venues", data.venueCreated, "leagues"],
      });
    },
  });
};

export const useUpdateLeagueMutation = (leagueUuid?: string, venueUuid?: string) => {
  return useMutation({
    mutationFn: (payload: ApiUpdateLeaguePayload) => {
      if (!leagueUuid) {
        throw new Error("Missing leagueUuid");
      }
      return api.competitions.updateLeague(leagueUuid, payload);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["leagues", data.uuid], data);
      queryClient.invalidateQueries({
        queryKey: ["venues", venueUuid, "leagues"],
      });
    },
  });
};

export const useDeleteLeagueMutation = (venueUuid?: string) => {
  return useMutation({
    mutationFn: (leagueUuid: string) => api.competitions.deleteLeague(leagueUuid),
    onSuccess: (_, leagueUuid) => {
      queryClient.invalidateQueries({
        queryKey: ["leagues", leagueUuid],
      });
      if (venueUuid) {
        queryClient.invalidateQueries({
          queryKey: ["venues", venueUuid, "leagues"],
        });
      }
    },
  });
};

export const useLeagueEntriesQuery = (
  leagueUuid?: string,
  select?: (data: ApiLeagueEntryInline[]) => ApiLeagueEntryInline[],
) => {
  return useQuery({
    queryKey: ["leagues", leagueUuid, "entries"],
    queryFn: () => api.competitions.getLeagueEntries(leagueUuid!),
    enabled: !!leagueUuid,
    select,
    refetchOnMount: "always",
  });
};

export const useLeagueEntryQuery = (
  leagueUuid?: string,
  userUuid?: string,
  select?: (data: ApiLeagueEntry | ApiLeagueEntryInline) => ApiLeagueEntry | ApiLeagueEntryInline,
) => {
  return useQuery({
    queryKey: ["leagues", leagueUuid, "entries", userUuid],
    queryFn: () =>
      api.competitions.getLeagueEntry(leagueUuid!, userUuid!) as Promise<ApiLeagueEntryInline>,
    enabled: !!leagueUuid && !!userUuid,
    select,
    placeholderData: () => {
      const entries = queryClient.getQueryData<ApiLeagueEntryInline[]>([
        "leagues",
        leagueUuid,
        "entries",
      ]);
      return entries?.find((entry) => entry.user.uuid === userUuid);
    },
    refetchOnMount: true,
  });
};

export const useCompetitionRouteCustomScoringConfigsQuery = <
  TData = ApiCompetitionRouteCustomScoringConfig[],
>(
  competitionUuid?: string,
  select?: (data: ApiCompetitionRouteCustomScoringConfig[]) => TData,
) => {
  return useQuery({
    queryKey: ["competitions", competitionUuid, "routeCustomScoringConfigs"],
    queryFn: () => api.competitions.getCompetitionRouteCustomScoringConfigs(competitionUuid!),
    enabled: !!competitionUuid,
    select,
  });
};

export const useCompetitionRouteCustomScoringConfigsByUuid = (competitionUuid?: string) => {
  const select = useCallback((data: ApiCompetitionRouteCustomScoringConfig[]) => {
    return Object.fromEntries(data.map((config) => [config.route, config.config]));
  }, []);
  return useQuery({
    queryKey: ["competitions", competitionUuid, "routeCustomScoringConfigs"],
    queryFn: () => api.competitions.getCompetitionRouteCustomScoringConfigs(competitionUuid!),
    enabled: !!competitionUuid,
    select,
  }).data;
};

export const useCompetitionUserLookupQuery = (competitionUuid?: string, searchQuery?: string) => {
  return useQuery({
    enabled: !!competitionUuid && !!searchQuery,
    queryKey: ["competitions", competitionUuid, "userLookup", searchQuery],
    queryFn: () => api.competitions.getCompetitionUserLookupMatches(competitionUuid!, searchQuery!),
    gcTime: 0,
  });
};

export const useMergeLeagueEntriesMutation = () => {
  return useMutation({
    mutationFn: ({
      leagueUuid,
      sourceUserUuid,
      targetUserUuid,
    }: {
      leagueUuid: string;
      sourceUserUuid: string;
      targetUserUuid: string;
    }) =>
      api.competitions.mergeLeagueEntries(leagueUuid, {
        sourceUser: sourceUserUuid,
        targetUser: targetUserUuid,
      }),
    onSuccess: (_, { leagueUuid, sourceUserUuid, targetUserUuid }) => {
      queryClient.invalidateQueries({
        queryKey: ["leagues", leagueUuid, "entries"],
      });
      queryClient.invalidateQueries({
        queryKey: ["leagues", leagueUuid, "entries", sourceUserUuid],
      });
      queryClient.invalidateQueries({
        queryKey: ["leagues", leagueUuid, "entries", targetUserUuid],
      });
      // Invalidate individual rounds too
      queryClient.invalidateQueries({
        predicate(query) {
          return (
            query.queryKey.length > 2 &&
            query.queryKey[0] === "competitions" &&
            (query.queryKey[2] === "entries" || query.queryKey[2] === "attempts")
          );
        },
      });
    },
  });
};

export const useSponsorsListQuery = () => {
  return useQuery({
    queryKey: ["competitions", "sponsorsList"],
    queryFn: async () => {
      const response = await api.competitions.getSponsorsList();
      return response.data;
    },
  });
};
